import React from "react";
import { Formik, Form, Field } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { addDdOption } from "../../../store/reducer/ddOption/ddOptionSlice";

function AddDdOption() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const validationSchema = Yup.object().shape({
    ui_name: Yup.string().required("please enter ui name"),
    field_name: Yup.string().required("please enter field name"),
  });

  // Tailwind color map (fixed classes)
  const colorMap = {
    slate: "bg-slate-500",
    gray: "bg-gray-500",
    zinc: "bg-zinc-500",
    neutral: "bg-neutral-500",
    stone: "bg-stone-500",
    red: "bg-red-500",
    orange: "bg-orange-500",
    amber: "bg-amber-500",
    yellow: "bg-yellow-400",
    lime: "bg-lime-500",
    green: "bg-green-500",
    emerald: "bg-emerald-500",
    teal: "bg-teal-500",
    cyan: "bg-cyan-500",
    sky: "bg-sky-500",
    blue: "bg-blue-500",
    indigo: "bg-indigo-500",
    violet: "bg-violet-500",
    purple: "bg-purple-500",
    fuchsia: "bg-fuchsia-500",
    pink: "bg-pink-500",
    rose: "bg-rose-500",
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    values.createdBy = JSON.parse(localStorage.getItem("userSession")).id;

    const formData = new FormData();
    Object.keys(values).forEach((fieldName) => {
      formData.append(fieldName, values[fieldName]);
    });

    await dispatch(addDdOption(formData)).then((result) => {
      if (result.payload) {
        resetForm();
        toast.success(result.payload.message);
      } else {
        toast.error(result.error.message);
      }
    });
    setSubmitting(false);
  };

  return (
    <>
      <div className="flex justify-between mb-[30px]">
        <div className="flex gap-[16px] items-center">
          <Link to="/setting/dd-option">
            <div className="bg-[#F8F8F8] rounded-[10px] p-[8px]">
              <img
                className="w-[24px]"
                src={process.env.PUBLIC_URL + "/icons/back.png"}
                alt="back icon"
              />
            </div>
          </Link>
          <div className="flex flex-col">
            <span className="font-Inter font-[400] text-[#9E9E9E] text-[14px]">
              DropDown / Add Option
            </span>
            <span className="font-Inter font-[700] text-[24px]">
              Add DropDown Option
            </span>
          </div>
        </div>
      </div>

      <Formik
        initialValues={{
          ui_name: "",
          field_name: "",
          field_code: "",
          field_value: "",
          field_desc: "",
          field_color: "",
          is_default: false,
          sort_field: 0,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, errors, touched, setFieldValue }) => (
          <Form>
            <div className="flex gap-[20px]">
              <div className="md:grid md:grid-cols-3 gap-[20px] bg-[#F8F8F8] rounded-[12px] p-6 w-full">
                {/* UI Name */}
                <div>
                  <label className="text-[#9E9E9E] text-[15px]">UI Name</label>
                  <Field
                    name="ui_name"
                    type="text"
                    className="w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px] focus:outline-none"
                  />
                  {errors.ui_name && touched.ui_name && (
                    <div className="text-red-600">{errors.ui_name}</div>
                  )}
                </div>

                {/* Field Name */}
                <div>
                  <label className="text-[#9E9E9E] text-[15px]">
                    Field Name
                  </label>
                  <Field
                    name="field_name"
                    type="text"
                    className="w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px] focus:outline-none"
                  />
                  {errors.field_name && touched.field_name && (
                    <div className="text-red-600">{errors.field_name}</div>
                  )}
                </div>

                {/* Field Code */}
                <div>
                  <label className="text-[#9E9E9E] text-[15px]">
                    Field Code
                  </label>
                  <Field
                    name="field_code"
                    type="text"
                    className="w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px] focus:outline-none"
                  />
                </div>

                {/* Field Value */}
                <div>
                  <label className="text-[#9E9E9E] text-[15px]">
                    Field Value
                  </label>
                  <Field
                    name="field_value"
                    type="text"
                    className="w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px] focus:outline-none"
                  />
                </div>

                {/* Field Color */}
                <div>
                  <label className="text-[#9E9E9E] text-[15px]">
                    Field Color
                  </label>
                  <div className="flex items-center gap-3">
                    <Field
                      as="select"
                      name="field_color"
                      className="w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px] focus:outline-none"
                      onChange={(e) =>
                        setFieldValue("field_color", e.target.value)
                      }
                    >
                      <option value="">Select a Color</option>
                      {Object.keys(colorMap).map((color) => (
                        <option key={color} value={color}>
                          {color.charAt(0).toUpperCase() + color.slice(1)}
                        </option>
                      ))}
                    </Field>

                    {/* Color preview circle */}
                    {values.field_color && (
                      <div
                        className={`w-8 h-8 rounded-full border border-gray-300 ${
                          colorMap[values.field_color]
                        }`}
                      ></div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-[#9E9E9E] text-[15px]">
                    Description
                  </label>
                  <Field
                    as="textarea"
                    name="field_desc"
                    className="w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px] focus:outline-none"
                  />
                </div>

                {/* Sort Field */}
                <div>
                  <label className="text-[#9E9E9E] text-[15px]">
                    Sort Field
                  </label>
                  <Field
                    name="sort_field"
                    type="number"
                    className="w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px] focus:outline-none"
                  />
                </div>

                {/* Is Default */}
                <div>
                  <label className="text-[#9E9E9E] text-[15px]">Is Default</label>
                  <div>
                    <label className="inline-flex items-center px-5 py-2 cursor-pointer text-gray-800 bg-white rounded-[12px]">
                      <input
                        id="is_default"
                        type="checkbox"
                        className="hidden peer"
                        name="is_default"
                        onChange={(e) =>
                          setFieldValue("is_default", e.target.checked)
                        }
                      />
                      <span className="px-4 py-1 rounded-lg border-gray-600 border peer-checked:border-0">
                        No
                      </span>
                      <span className="px-4 py-1 rounded-lg border-gray-600 peer-checked:border">
                        Yes
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-[24px] bg-[#F8F8F8] rounded-[12px] mt-5 p-4">
              <div>
                <button
                  type="submit"
                  className={`py-[12px] px-[48px] text-white w-full rounded-[12px] text-[18px] ${
                    isSubmitting ? "bg-gray-300" : "bg-[#1C1C20]"
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}

export default AddDdOption;
